import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Table, Select, Modal, DatePicker, Button, Collapse, message } from "antd";
import moment from "moment";
import ENVIRONMENT from "../../../../environments";
import axios from "axios";
import { LoadingOutlined, FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import { exportToCSV, generateExportFilename } from "../../Utilities";
import { withRouter } from "react-router-dom";

const Actions = {
  get_system_summary: "get_system_summary",
  get_model_details: "get_model_details",
};

const DATE_FORMAT = "YYYY-MM-DD";
const ALL_TIME_START = "2022-01-01";
const { RangePicker } = DatePicker;

const MODELS_ORDER = [
  'Product Model AR Conversion Variation',
  'Product Model Bedding Variation',
  'Product Model Color Variation',
  'Product Customer Model Fix',
  'Product Model Hardware Variation',
  'Product Model Material Variation',
  'Product Model Other Variation',
  'Product Model Size Variation',
  'Product Model State Variation',
  'Product Model (VRAY + AR)',
  'Product Model (VRAY)',
  'Product Model (AR)',
  'High Poly PBR',
  'User Uploaded Model',
  'Space Model Variation',
  'Space Base Model',
  'Material',
]

const CostSummary = ({ companies = [], modelStatus, history, location }) => {
  
  const [isSummaryFetched, setIsSummaryFetched] = useState(false);
  const [costSummary, setCostSummary] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().subtract(1, 'months').startOf("month").format(DATE_FORMAT)
  );
  const [endDate, setEndDate] = useState(
    moment().subtract(1, 'months').endOf("month").format(DATE_FORMAT)
  );
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]); // Store all customers for filtering
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [companiesData, setCompaniesData] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [categories, setCategories] = useState([]);
  const [customStartDate, setCustomStartDate] = useState(
      moment().subtract(1,"months").startOf('month')
  );
  const [customEndDate, setCustomEndDate] = useState(
      moment()
  );
  const [selectedValue, setSelectedValue] = useState("1");
  const [isModelDetailsModalVisible, setIsModelDetailsModalVisible] = useState(false);
  const [isModelsDetailFetched, setIsModelsDetailFetched] = useState(false)
  const [modelDetails, setModelDetails] = useState([]);
  const [modelType, setModelType] = useState(null);
  const [modelCount, setModelCount] = useState(0);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const getCostSummary = React.useCallback(() => {
    console.log("Start Date", startDate);
    console.log("End Date", endDate);
    let payload = {
        "action" : Actions["get_system_summary"],
        "start_date" : startDate,
        "end_date" : endDate,
        "is_approved" : modelStatus == 'approved'
    };

    if (selectedCustomer && selectedCustomer !== "all") {
      payload["customer"] = selectedCustomer
    }

    // Convert company value to proper format for API
    const companyId = getCompanyIdForAPI(selectedCompany);
    if (companyId != null) {
      payload["company_id"] = companyId
    }

    if (selectedCategories && Array.isArray(selectedCategories) && selectedCategories.length > 0) {
      payload.category = selectedCategories;
    }

    console.log("Payload", payload);
    
    setIsSummaryFetched(true);
    axios.post(ENVIRONMENT.GET_MODELING_COST_REPORT, payload).then((response) => {
        const responseData = response.data;
        console.log('Cost Summary', responseData);
        const orderedData = responseData.sort((a, b) => {
          const indexA = MODELS_ORDER.indexOf(a.model_type);
          const indexB = MODELS_ORDER.indexOf(b.model_type);
      
          if (indexA < indexB) return -1;
          if (indexA > indexB) return 1;
          return 0;
        });
        setCostSummary(orderedData);
        setIsSummaryFetched(false);
    });
  }, [startDate, endDate, selectedCustomer, selectedCompany, selectedCategories, modelStatus]);

  // Parse URL query parameters for CostSummary
  const parseURLParams = () => {
    const searchParams = new URLSearchParams(location.search);
    
    // Get the date range selection first
    const selectedValue = searchParams.get('cs_dr') || "1";
    
    // Calculate start and end dates based on the selected range
    let startDate, endDate;
    
    if (selectedValue === "all_time") {
      startDate = ALL_TIME_START;
      endDate = moment().format(DATE_FORMAT);
    } else if (selectedValue === "custom") {
      // For custom dates, use the stored dates from URL
      startDate = searchParams.get('cs_sd') || moment().subtract(1, 'months').startOf("month").format(DATE_FORMAT);
      endDate = searchParams.get('cs_ed') || moment().subtract(1, 'months').endOf("month").format(DATE_FORMAT);
    } else {
      // For predefined ranges, calculate the dates
      const numberOfMonths = parseInt(selectedValue, 10);
      startDate = moment().subtract(numberOfMonths, 'months').startOf("month").format(DATE_FORMAT);
      endDate = moment().subtract(numberOfMonths, 'months').endOf("month").format(DATE_FORMAT);
    }
    
    return {
      startDate: startDate,
      endDate: endDate,
      selectedCustomer: searchParams.get('cs_c') || "all",
      selectedCompany: searchParams.get('cs_cp') || "all",
      selectedCategories: (searchParams.get('cs_cat') || "")
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0),
      selectedValue: selectedValue
    };
  };

  // Update URL with current filter state
  const updateURL = (filters) => {
    const searchParams = new URLSearchParams(location.search);
    
    // Update CostSummary params - only store dates for custom selection
    if (filters.startDate && filters.selectedValue === 'custom') {
      searchParams.set('cs_sd', filters.startDate);
    } else {
      searchParams.delete('cs_sd');
    }
    
    if (filters.endDate && filters.selectedValue === 'custom') {
      searchParams.set('cs_ed', filters.endDate);
    } else {
      searchParams.delete('cs_ed');
    }
    
    // Handle customer filter
    if (filters.selectedCustomer !== undefined) {
      if (filters.selectedCustomer && filters.selectedCustomer !== "all") {
        searchParams.set('cs_c', filters.selectedCustomer);
      } else {
        searchParams.delete('cs_c');
      }
    }
    
    // Handle company filter
    if (filters.selectedCompany !== undefined) {
      if (filters.selectedCompany && filters.selectedCompany !== "all") {
        searchParams.set('cs_cp', filters.selectedCompany);
      } else {
        searchParams.delete('cs_cp');
      }
    }
    
    if (filters.selectedCategories !== undefined) {
      const list = Array.isArray(filters.selectedCategories) ? filters.selectedCategories : [];
      if (list.length > 0) {
        searchParams.set('cs_cat', list.join(','));
      } else {
        searchParams.delete('cs_cat');
      }
    }

    // Handle date range filter - only store the range value, not the actual dates
    if (filters.selectedValue !== undefined) {
      searchParams.set('cs_dr', filters.selectedValue);
    }

    const newURL = `${location.pathname}?${searchParams.toString()}`;
    history.replace(newURL);
  };

  // Initialize state from URL params
  useEffect(() => {
    const urlParams = parseURLParams();
    setStartDate(urlParams.startDate);
    setEndDate(urlParams.endDate);
    setSelectedCustomer(urlParams.selectedCustomer);
    setSelectedCompany(urlParams.selectedCompany);
    setSelectedCategories(urlParams.selectedCategories);
    setSelectedValue(urlParams.selectedValue);
    
    // Set custom dates if needed
    if (urlParams.selectedValue === 'custom') {
      setCustomStartDate(moment(urlParams.startDate));
      setCustomEndDate(moment(urlParams.endDate));
    }
    
    // Mark as initialized after setting up the initial state
    setIsInitialized(true);
  }, []);

  // Trigger API call when filters change or on initial load
  useEffect(() => {
    if (isInitialized && startDate && endDate) {
      getCostSummary();
    }
  }, [isInitialized, startDate, endDate, selectedCustomer, selectedCompany, selectedCategories, getCostSummary, modelStatus]);



  // Handle company value conversion for API calls
  const getCompanyIdForAPI = (companyValue) => {
    if (!companyValue || companyValue === "all") return null;
    
    return companyValue;
  };

  // Helper function to get company display name from ID for dropdown display
  const getCompanyDisplayName = (companyValue) => {
    if (!companyValue || companyValue === "all") return "all";
    
    const company = companies.find(c => c.value === companyValue);
    return company ? company.label : undefined;
  };

  // Table Columns
  const costSummaryColumns = [
    {
      title: "Model Type",
      dataIndex: "model_type",
      key: "model_type"
    },
    {
      title: "Average Artist Cost",
      dataIndex: "avg_modeling_cost",
      key: "avg_modeling_cost",
      render: (text) => '$'+text,
    },
    {
      title: "Average Automation Cost",
      dataIndex: "avg_automation_cost",
      key: "avg_automation_cost",
      render: (text) => '$'+text,
    },
    {
      title: "Average Total Cost",
      dataIndex: "avg_cost",
      key: "avg_cost",
      render: (text) => '$'+text,
    },
    {
      title: "Frequency/Count",
      dataIndex: "frequency",
      key: "frequency",
      render: (text, record) => <a
      className="onhover-underline"
      onClick={() => {
        setModelCount(text);
        showModelDetails(record.model_type);
      }}>{text}</a>,
    },
  ]

  const costDetailsColumns = [
    {
      title: "Model ID",
      dataIndex: "id",
      key: "id",
      render: (text, record) => (
        <a
          className="onhover-underline"
          href={getModelLink(text, record.model_type)}>
        {text}
        </a>
      ),
    },
    {
      title: "Model Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Username",
      dataIndex: "customer_username",
      key: "customer_username",
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Artist Cost",
      dataIndex: "artist_modeling_cost",
      key: "artist_modeling_cost",
      render: (text) => '$'+text,
    },
    {
      title: "Automation Cost",
      dataIndex: "automation_cost",
      key: "automation_cost",
      render: (text) => '$'+text,
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      render: (text) => '$'+text,
    },
  ]

  // Select (Date Dropdown) functions
  const dateOptions = [
    {
        label: "Current Month",
        value: "0"
    },
    {
        label: "Last Month",
        value: "1"
    },
    {
        label: moment().subtract(2, 'months').format('MMMM YYYY'),
        value: "2"
    },
    {
      label: moment().subtract(3, 'months').format('MMMM YYYY'),
      value: "3"
    },
    {
      label: "All Time",
      value: "all_time"
    },
    {
        label: "Custom",
        value: "custom"
    }
  ];

  const handleDateChange = (value) => {
    console.log("Date Value: ", value);
    let newStartDate, newEndDate;
    
    if (value === "all_time") {
      setSelectedValue(value);
      newStartDate = ALL_TIME_START;
      newEndDate = moment().format(DATE_FORMAT);
    }
    else if (value == "custom") {
      console.log("handleDateSelect")
      return;
    }
    else {
      setSelectedValue(value);
      const numberOfMonths = parseInt(value, 10);
      newStartDate = moment().subtract(numberOfMonths, 'months').startOf("month").format(DATE_FORMAT);
      newEndDate = moment().subtract(numberOfMonths, 'months').endOf("month").format(DATE_FORMAT);
    }
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    
    // Update URL - only store the date range value, not the actual dates
    updateURL({
      selectedValue: value
    });
  };

  const handleDateSelect = (value) => {
    console.log("Date Value: ", value);
    if (value == "custom") {
      setSelectedValue(value);
    }
  };

  // Select (Customers Dropdown) functions
  const getUsersList = useCallback(() => {
    let payload = {};
    axios.post(ENVIRONMENT.CUSTOMER_GET_BATCH, payload).then((response) => {
      console.log("Customers Data",response.data)
      let users = [{label : "Default (All Customers)", value: "all"}];
      if (response.data) {
        response.data.map((request) => {
          users.push({
            label: request.customer_username, 
            value: request.customer_username,
            company_id: request.company_id // Store company_id for filtering
          });
        });
        setAllCustomers(users);
        setCustomers(users); // Initially show all customers
        console.log("Users List", users);
      }
    });
  }, []);

  // Filter customers based on selected company
  const filterCustomersByCompany = useCallback((companyId) => {
    if (!companyId || companyId === "all") {
      setCustomers(allCustomers); // Show all customers
    } else {
      const filteredCustomers = allCustomers.filter(customer => 
        customer.value === "all" || customer.company_id === companyId
      );
      setCustomers(filteredCustomers);
    }
  }, [allCustomers]);

  const handleCustomerChange = (value) => {
    if (value === undefined || value === "all") {
      setSelectedCustomer("all");
    } else {
      setSelectedCustomer(value);
    }
    
    // Reset category selection when customer changes
    setSelectedCategories([]);
    
    // Update URL
    updateURL({ selectedCustomer: value === undefined ? "all" : value });
  };

  // Select (Companies Dropdown) functions
  const getCompaniesList = () => {
    let payload = {};
    axios.post(ENVIRONMENT.COMPANY_GET_BATCH, payload).then((response) => {
      console.log("Companies Data", response.data);
      setCompaniesData(response.data);
    });
  };

  const handleCompanyChange = (value) => {
    if (value === undefined || value === "all") {
      setSelectedCompany("all");
    } else {
      setSelectedCompany(value);
    }
    
    // Reset customer and category selection when company changes
    setSelectedCustomer("all");
    setSelectedCategories([]);
    
    // Filter customers for the selected company
    if (value === undefined || value === "all") {
      filterCustomersByCompany("all");
    } else {
      filterCustomersByCompany(value);
    }
    
    // Update URL with company ID
    if (value === undefined || value === "all") {
      updateURL({ selectedCompany: "all" });
    } else {
      updateURL({ selectedCompany: value });
    }
  };

  // Select (Categories Dropdown) functions
  const getCategoryList = useCallback(() => {
    let payload = {};
    
    // Add company_id and customer if selected
    if (selectedCompany && selectedCompany !== "all") {
      payload.company_id = selectedCompany;
    }
    if (selectedCustomer && selectedCustomer !== "all") {
      payload.customer_username = selectedCustomer;
    }
    
    axios.post(ENVIRONMENT.GET_ALL_CATEGORIES, payload).then((response) => {
      console.log("Categories Data", response.data);
      let categoriesData = [];
      if (response.data) {
        response.data.map((request) => {
          categoriesData.push({label:request.category, value:request.value});
        });
        setCategories(categoriesData);
        console.log("Categories List", categoriesData);
      }
    });
  }, [selectedCompany, selectedCustomer]);

  const handleCategoryChange = (value) => {
    const list = Array.isArray(value) ? value.filter((v) => v) : [];
    setSelectedCategories(list);
    
    // Update URL
    updateURL({ selectedCategories: list });
  };

  useEffect(() => {
    getUsersList();
    getCompaniesList(); // Keep this to get companiesData for company ID lookup
  }, []);

  // Filter customers when company changes (for initial load from URL params)
  useEffect(() => {
    if (isInitialized && selectedCompany && selectedCompany !== "all") {
      filterCustomersByCompany(selectedCompany);
    }
  }, [isInitialized, selectedCompany, filterCustomersByCompany]);

  // Fetch categories when company or customer changes
  useEffect(() => {
    if (isInitialized) {
      getCategoryList();
    }
  }, [isInitialized, selectedCompany, selectedCustomer]);

  // Fetch default categories when no filters are applied (initial load)
  useEffect(() => {
    if (isInitialized && (!selectedCompany || selectedCompany === "all") && (!selectedCustomer || selectedCustomer === "all")) {
      // Only fetch default categories if no specific filters are applied
      const payload = {};
      axios.post(ENVIRONMENT.GET_ALL_CATEGORIES, payload).then((response) => {
        console.log("Default Categories Data", response.data);
        let categoriesData = [];
        if (response.data) {
          response.data.map((request) => {
            categoriesData.push({label:request.category, value:request.value});
          });
          setCategories(categoriesData);
          console.log("Default Categories List", categoriesData);
        }
      });
    }
  }, [isInitialized, selectedCompany, selectedCustomer]);

  const handleCustomDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const start = dates[0];
      const end = dates[1];
      if (start && end && !start.isAfter(end)) {
        setCustomStartDate(start);
        setCustomEndDate(end);
        const newStartDate = start.format(DATE_FORMAT);
        const newEndDate = end.format(DATE_FORMAT);
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        
        // Update URL - store both the custom dates and the selectedValue
        updateURL({
          startDate: newStartDate,
          endDate: newEndDate,
          selectedValue: 'custom'
        });
      }
    }
  };

  const handleDisabledDates = (date) => {
    if (date.format(DATE_FORMAT) < ALL_TIME_START || date.format(DATE_FORMAT) > moment().format(DATE_FORMAT)) {
        return true;
    }
    return false;
  };

  const showModelDetails = (model_type) => {
    setModelType(model_type);
    setIsModelDetailsModalVisible(true);
    let payload = {
      "action" : Actions["get_model_details"],
      "start_date" : startDate,
      "end_date" : endDate,
      "model_type" : model_type,
      "is_approved" : modelStatus == 'approved'
    };

    if (selectedCustomer && selectedCustomer !== "all") {
      payload["customer"] = selectedCustomer
    }

    if (selectedCompany && selectedCompany !== "all") {
      payload["company_id"] = selectedCompany
    }

    if (selectedCategories && Array.isArray(selectedCategories) && selectedCategories.length > 0) {
      payload["category"] = selectedCategories
    }

    console.log("Payload", payload);
    
    setIsModelsDetailFetched(true);
    axios.post(ENVIRONMENT.GET_MODELING_COST_REPORT, payload).then((response) => {
        const responseData = response.data;
        console.log('Model Details', responseData);
        setModelDetails(responseData);
        setIsModelsDetailFetched(false);
    });
  };

  const closeModelDetails = () => {
    setIsModelDetailsModalVisible(false);
  };

  const getModelLink = (id, model_type)  => {
    if (model_type === 'Material') {
      return '/admin_materials/' + id
    } 
    else if (model_type === 'Space Model Variation' || model_type === 'Space Base Model') {
      return '/admin_rooms/' + id
    }
    else {
      return '/admin_products/' + id
    }
  }

  const handleExport = () => {
    if (!costSummary || costSummary.length === 0) {
      message.warning('No data to export');
      return;
    }

    const filename = generateExportFilename('Cost-summary', startDate, endDate);
    
    // Define export columns for the summary table
    const exportColumns = [
      { title: 'Model Type', dataIndex: 'model_type' },
      { title: 'Average Artist Cost', dataIndex: 'avg_modeling_cost' },
      { title: 'Average Automation Cost', dataIndex: 'avg_automation_cost' },
      { title: 'Average Total Cost', dataIndex: 'avg_cost' },
      { title: 'Frequency/Count', dataIndex: 'frequency' },
    ];

    // Custom value formatter for currency fields
    const valueFormatter = (value, row, col) => {
      if (col.dataIndex === 'avg_modeling_cost' || 
          col.dataIndex === 'avg_automation_cost' || 
          col.dataIndex === 'avg_cost') {
        return value ? `$${value}` : '$0';
      }
      return value;
    };

    exportToCSV(filename, exportColumns, costSummary, {
      valueFormatter: valueFormatter
    });
  };

  return (
    <>
      <Card
      className="no-hover section-container"
      size="small"
      title={
        <div>
          <Row className="mt-10 justify-in-start" gutter={12}>
            <Col span={12}>
              <span className="sub-heading">Summary Table</span>
            </Col>
            <Col span={12} className="justify-in-end">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                style={{ marginRight: 8 }}
                disabled={!costSummary || costSummary.length === 0}
              >
                Export CSV
              </Button>
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => setFiltersVisible(!filtersVisible)}
              >
                {filtersVisible ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Col>
          </Row>
          
          {filtersVisible && (
            <Row gutter={12} style={{ marginTop: 16 }}>
              <Col span={6}>
                <Select
                  showSearch
                  placeholder="Select Company"
                  style={{width: "100%"}}
                  options={companies || []}
                  onChange={handleCompanyChange}
                  optionFilterProp="label"
                  value={getCompanyDisplayName(selectedCompany)}
                  allowClear
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              <Col span={6}>
                <Select
                  showSearch
                  placeholder="Select Customer"
                  style={{width: "100%"}}
                  options={customers}
                  onChange={handleCustomerChange}
                  value={selectedCustomer}
                  allowClear
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              <Col span={6}>
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Default (All Categories)"
                  style={{width: "100%"}}
                  options={categories}
                  onChange={handleCategoryChange}
                  value={selectedCategories}
                  allowClear
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Select Date"
                  style={{width: "100%"}}
                  options={dateOptions}
                  defaultValue={dateOptions[1]["value"]}
                  onChange={handleDateChange}
                  onSelect={handleDateSelect}
                  value={selectedValue}
                  className="analytics-filter-centered-search-select"
                />
              </Col>
              {selectedValue === 'custom' && (
                <Col span={6} style={{ marginTop: 12 }}>
                  <RangePicker
                    value={customStartDate && customEndDate ? [customStartDate, customEndDate] : null}
                    onChange={handleCustomDateChange}
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    disabledDate={handleDisabledDates}
                    style={{ width: "100%" }}
                  />
                </Col>
              )}
            </Row>
          )}
        </div>
      }>
        <Row>
            <div className="chart-container">
                {
                isSummaryFetched ? (<LoadingOutlined className="loading-center top-20"/>) : 
                <Table
                dataSource={costSummary}
                columns={costSummaryColumns}
                scroll
                style={{ overflowX: "scroll" }}
                bordered={true}
                pagination={{
                  pageSize: 25
                }}
                />
                }
            </div>
        </Row>
      </Card>
      <Modal
      title={`${modelType} Details (${modelCount})`}
      visible={isModelDetailsModalVisible}
      onCancel={closeModelDetails}
      footer={null}
      width={"70%"}
      bodyStyle={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Row>
          <div className="chart-container">
              {
              isModelsDetailFetched ? (<LoadingOutlined className="loading-center top-20"/>) : 
              <Table
              dataSource={modelDetails}
              columns={costDetailsColumns}
              scroll
              style={{ overflowX: "scroll" }}
              />
              }
          </div>
        </Row>
      </Modal>
    </>
  );
};

export default withRouter(CostSummary);